import React, { useEffect, useState } from "react";
import {
  Drawer,
  Input,
  Select,
  Slider,
  Space,
  Button,
  Alert,
  Switch,
  Segmented,
  notification,
} from "antd";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  createRule,
  updateRule,
  fetchSettings,
  fetchTemplates,
} from "./service";
import { INSIGHT_SIGNALS, APPROVAL_MODE_DESCRIPTION_KEYS, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "./constants";
import { languageForUi } from "./languageUtils";
import styles from "./style.module.scss";

const TRIGGER_OPTIONS = [
  { value: "insight", labelKey: "sonic.trigger_pivony_insight" },
  { value: "process", labelKey: "sonic.trigger_webhook" },
  { value: "manual", labelKey: "sonic.trigger_manual" },
];

const CHANNEL_OPTIONS = [
  { value: "whatsapp", labelKey: "sonic.channel_whatsapp" },
  { value: "voice_call", labelKey: "sonic.channel_voice" },
];

const APPROVAL_OPTIONS = [
  { value: "automatic", labelKey: "sonic.approval_auto_fire" },
  { value: "manual", labelKey: "sonic.approval_requires" },
  { value: "hybrid", labelKey: "sonic.approval_hybrid" },
];

const emptyState = {
  name: "",
  trigger_type: "insight",
  signal: "nps_score",
  npsRange: [0, 6],
  vipFlag: false,
  sentimentThreshold: 0.7,
  eventName: "",
  channel: "whatsapp",
  language: DEFAULT_LANGUAGE,
  template_id: undefined,
  approval_mode: "automatic",
  active: true,
};

const RuleBuilderDrawer = ({ open, rule, onClose, onSaved }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [form, setForm] = useState(emptyState);
  const [saving, setSaving] = useState(false);
  const [waConnected, setWaConnected] = useState(true);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!open) return;
    fetchSettings().then((res) => {
      if (res && !res.error && res.settings) {
        setWaConnected(
          !!res.settings.whatsapp_connected || !!res.settings.whatsapp_configured
        );
      }
    });
    fetchTemplates().then((res) => {
      if (res && !res.error) setTemplates(res.templates || []);
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (rule) {
      const tc = rule.trigger_config || {};
      setForm({
        name: rule.name || "",
        trigger_type: rule.trigger_type || "insight",
        signal: tc.signal || "nps_score",
        npsRange: [
          tc.threshold_min != null ? tc.threshold_min : 0,
          tc.threshold_max != null ? tc.threshold_max : 6,
        ],
        vipFlag: !!tc.vip_flag,
        sentimentThreshold: tc.sentiment_threshold != null ? tc.sentiment_threshold : 0.7,
        eventName: tc.event_name || "",
        channel: rule.channel || "whatsapp",
        language: rule.language || DEFAULT_LANGUAGE,
        template_id: rule.template_id || undefined,
        approval_mode: rule.approval_mode || "automatic",
        active: rule.status === "active",
      });
    } else {
      setForm({ ...emptyState, language: languageForUi(i18n.language) });
    }
  }, [open, rule]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const buildPayload = () => {
    let trigger_config = {};
    if (form.trigger_type === "insight" || form.trigger_type === "manual") {
      trigger_config = { signal: form.signal };
      if (form.signal === "nps_score") {
        trigger_config.threshold_min = form.npsRange[0];
        trigger_config.threshold_max = form.npsRange[1];
      }
      if (form.signal === "sentiment_spike") {
        trigger_config.sentiment_threshold = form.sentimentThreshold;
      }
      if (form.signal === "vip_complaint") {
        trigger_config.vip_flag = form.vipFlag;
      }
    } else if (form.trigger_type === "process") {
      trigger_config = { event_name: form.eventName };
    }
    return {
      name: form.name.trim(),
      trigger_type: form.trigger_type === "manual" ? "insight" : form.trigger_type,
      trigger_config,
      condition_config: {},
      channel: form.channel,
      language: form.language,
      template_id: form.template_id || null,
      approval_mode: form.approval_mode,
      status: form.active ? "active" : "paused",
    };
  };

  const validate = () => {
    if (!form.name.trim()) {
      notification.warning({ message: t("sonic.name_required") });
      return false;
    }
    if (form.trigger_type === "process" && !form.eventName.trim()) {
      notification.warning({ message: t("sonic.event_required") });
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = buildPayload();
    const res = rule
      ? await updateRule(rule.id, payload)
      : await createRule(payload);
    setSaving(false);
    if (res && !res.error) {
      notification.success({
        message: rule ? t("sonic.rule_updated") : t("sonic.rule_created"),
      });
      onSaved();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  return (
    <Drawer
      title={rule ? t("sonic.edit_rule") : t("sonic.new_rule")}
      open={open}
      onClose={onClose}
      width={480}
      className={styles.builderDrawer}
      footer={
        <div className={styles.drawerFooter}>
          <Button onClick={onClose}>{t("sonic.cancel")}</Button>
          <Button type="primary" loading={saving} onClick={save}>
            {t("sonic.save_rule")}
          </Button>
        </div>
      }
    >
      <div className={styles.builderBody}>
        <section className={styles.builderSection}>
          <label className={styles.fieldLabel}>{t("sonic.rule_name")}</label>
          <Input
            value={form.name}
            placeholder={t("sonic.rule_name_ph")}
            onChange={(e) => set({ name: e.target.value })}
          />
        </section>

        <section className={styles.builderSection}>
          <label className={styles.fieldLabel}>{t("sonic.trigger_type")}</label>
          <Select
            style={{ width: "100%" }}
            value={form.trigger_type}
            onChange={(v) => set({ trigger_type: v })}
            options={TRIGGER_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
          />
        </section>

        {(form.trigger_type === "insight" || form.trigger_type === "manual") && (
          <section className={styles.builderSection}>
            <label className={styles.fieldLabel}>{t("sonic.trigger_condition")}</label>
            <Select
              style={{ width: "100%" }}
              value={form.signal}
              onChange={(v) => set({ signal: v })}
              options={INSIGHT_SIGNALS.map((s) => ({ value: s.value, label: t(s.labelKey) }))}
            />
            {form.signal === "nps_score" && (
              <div style={{ marginTop: 12 }}>
                <label className={styles.fieldLabel}>
                  {t("sonic.nps_range")}: {form.npsRange[0]} – {form.npsRange[1]}
                </label>
                <Slider
                  range
                  min={0}
                  max={10}
                  value={form.npsRange}
                  onChange={(v) => set({ npsRange: v })}
                />
              </div>
            )}
            {form.signal === "sentiment_spike" && (
              <div style={{ marginTop: 12 }}>
                <label className={styles.fieldLabel}>
                  {t("sonic.sentiment_threshold")}: {form.sentimentThreshold}
                </label>
                <Slider
                  min={0.1}
                  max={1}
                  step={0.1}
                  value={form.sentimentThreshold}
                  onChange={(v) => set({ sentimentThreshold: v })}
                />
              </div>
            )}
            {form.signal === "vip_complaint" && (
              <div className={styles.toggleRow} style={{ marginTop: 12 }}>
                <span className={styles.fieldLabel}>{t("sonic.vip_flag")}</span>
                <Switch checked={form.vipFlag} onChange={(v) => set({ vipFlag: v })} />
              </div>
            )}
          </section>
        )}

        {form.trigger_type === "process" && (
          <section className={styles.builderSection}>
            <label className={styles.fieldLabel}>{t("sonic.event_name")}</label>
            <Input
              value={form.eventName}
              placeholder="order.delivered"
              onChange={(e) => set({ eventName: e.target.value })}
            />
          </section>
        )}

        <section className={styles.builderSection}>
          <label className={styles.fieldLabel}>{t("sonic.language")}</label>
          <Select
            style={{ width: "100%" }}
            value={form.language}
            onChange={(v) => set({ language: v, template_id: undefined })}
            options={SUPPORTED_LANGUAGES}
          />
          <p className={styles.mutedNote}>{t("sonic.rule_language_desc")}</p>
        </section>

        <section className={styles.builderSection}>
          <label className={styles.fieldLabel}>{t("sonic.col_channel")}</label>
          <Segmented
            block
            value={form.channel}
            onChange={(v) => set({ channel: v })}
            options={CHANNEL_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
          />
          {form.channel === "whatsapp" && !waConnected && (
            <Alert
              className={styles.channelAlert}
              type="warning"
              showIcon
              message={t("sonic.whatsapp_not_connected_title")}
              description={t("sonic.whatsapp_not_connected_desc")}
              action={
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    onClose();
                    history.push("/console/sonic?tab=settings");
                  }}
                >
                  {t("sonic.go_to_settings")}
                </Button>
              }
            />
          )}
        </section>

        <section className={styles.builderSection}>
          <label className={styles.fieldLabel}>{t("sonic.col_template")}</label>
          <Select
            style={{ width: "100%" }}
            allowClear
            placeholder={t("sonic.select_template")}
            value={form.template_id}
            onChange={(v) => set({ template_id: v })}
            options={templates
              .filter(
                (tpl) =>
                  !form.language || (tpl.language || DEFAULT_LANGUAGE) === form.language
              )
              .map((tpl) => ({
                value: tpl.id,
                label: `${tpl.name} (${tpl.language || DEFAULT_LANGUAGE})`,
              }))}
          />
        </section>

        <section className={styles.builderSection}>
          <label className={styles.fieldLabel}>{t("sonic.section_approval")}</label>
          <Segmented
            block
            value={form.approval_mode}
            onChange={(v) => set({ approval_mode: v })}
            options={APPROVAL_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
          />
          <p className={styles.mutedNote}>
            {t(APPROVAL_MODE_DESCRIPTION_KEYS[form.approval_mode])}
          </p>
        </section>

        <section className={styles.builderSection}>
          <div className={styles.toggleRow}>
            <div>
              <div className={styles.toggleTitle}>{t("sonic.rule_active")}</div>
              <div className={styles.toggleDesc}>{t("sonic.rule_active_desc")}</div>
            </div>
            <Switch checked={form.active} onChange={(v) => set({ active: v })} />
          </div>
        </section>
      </div>
    </Drawer>
  );
};

export default RuleBuilderDrawer;
